import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import MetricsCard from './components/MetricsCard';
import ProjectCard from './components/ProjectCard';
import DeadlineItem from './components/DeadlineItem';
import RevenueChart from './components/RevenueChart';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PermissionDenied from '../../components/ui/PermissionDenied';
import SkeletonCard from '../../components/ui/SkeletonCard';
import EmptyState from '../../components/ui/EmptyState';
import { usePermissions } from '../../hooks/usePermissions';
import { projectService } from '../../services/projectService';
import { permitService } from '../../services/permitService';
import { calendarService } from '../../services/calendarService';
import { customerService } from '../../services/customerService';
import { supabase } from '../../lib/supabase';
import SEO from '../../components/SEO';

const Dashboard = () => {
  const navigate = useNavigate();
  const { canCreate } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPermDenied, setShowPermDenied] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [projectsData, permitsData, eventsData, customersData, activityData] = await Promise.all([
        projectService?.getAll()?.catch(() => []),
        permitService?.getAll()?.catch(() => []),
        calendarService?.getAll()?.catch(() => []),
        customerService?.getAll()?.catch(() => []),
        fetchRecentActivity()
      ]);

      setProjects(projectsData || []);
      setCustomers(customersData || []);
      setRecentActivity(activityData || []);

      const deadlines = computeUpcomingDeadlines(permitsData || [], eventsData || []);
      setUpcomingDeadlines(deadlines);

      const revenue = computeRevenueData(projectsData || []);
      setRevenueData(revenue);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return [];

      // Get current user's organization_id
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('organization_id')
        ?.eq('id', session?.user?.id)
        ?.maybeSingle();

      if (!profile?.organization_id) return [];

      // Fetch activity logs only for users in the same organization
      const { data: orgUsers } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('organization_id', profile?.organization_id);

      const orgUserIds = (orgUsers || [])?.map(u => u?.id);
      if (!orgUserIds?.length) return [];

      const { data, error } = await supabase
        ?.from('activity_logs')
        ?.select('*, user_profiles(full_name)')
        ?.in('user_id', orgUserIds)
        ?.order('created_at', { ascending: false })
        ?.limit(5);
      if (error) return [];
      return data || [];
    } catch {
      return [];
    }
  };

  const computeRevenueData = (projectsList) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months?.push({ month: monthNames?.[d?.getMonth()], year: d?.getFullYear(), monthIndex: d?.getMonth(), revenue: 0 });
    }

    projectsList?.forEach(project => {
      const createdAt = project?.createdAt ? new Date(project.createdAt) : null;
      if (!createdAt) return;
      const budget = parseFloat(project?.budgetTotal || 0);
      months?.forEach(m => {
        if (createdAt?.getFullYear() === m?.year && createdAt?.getMonth() === m?.monthIndex) {
          m.revenue += budget;
        }
      });
    });

    // If no real revenue data, show a meaningful fallback
    const hasData = months?.some(m => m?.revenue > 0);
    if (!hasData) {
      return months?.map((m, i) => ({ month: m?.month, revenue: 0 }));
    }

    return months?.map(m => ({ month: m?.month, revenue: Math.round(m?.revenue) }));
  };

  const computeUpcomingDeadlines = (permits, events) => {
    const today = new Date();
    const deadlines = [];

    permits?.forEach(permit => {
      // Use 'deadline' field (maps from DB column 'deadline')
      const deadlineField = permit?.deadline;
      if (deadlineField) {
        const deadlineDate = new Date(deadlineField);
        if (deadlineDate >= today) {
          const daysLeft = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
          deadlines?.push({
            id: `permit-${permit?.id}`,
            type: 'permit',
            permitId: permit?.id,
            title: `${permit?.permitType || 'Permit'} Deadline`,
            project: permit?.projectName || permit?.projects?.customerName || 'Unknown Project',
            date: deadlineDate?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            daysLeft: `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
            urgency: daysLeft <= 7 ? 'critical' : daysLeft <= 14 ? 'high' : daysLeft <= 30 ? 'medium' : 'low',
            sortDate: deadlineDate
          });
        }
      }
    });

    events?.forEach(event => {
      if (event?.startDate) {
        const eventDate = new Date(event?.startDate);
        if (eventDate >= today) {
          const daysLeft = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
          deadlines?.push({
            id: `event-${event?.id}`,
            type: 'event',
            eventId: event?.id,
            title: event?.title || 'Event',
            project: event?.projectName || 'Calendar Event',
            date: eventDate?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            daysLeft: `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
            urgency: daysLeft <= 7 ? 'critical' : daysLeft <= 14 ? 'high' : daysLeft <= 30 ? 'medium' : 'low',
            sortDate: eventDate
          });
        }
      }
    });

    return deadlines?.sort((a, b) => a?.sortDate - b?.sortDate)?.slice(0, 5);
  };

  const handleNewProject = () => {
    if (!canCreate) {
      setShowPermDenied(true);
      setTimeout(() => setShowPermDenied(false), 4000);
      return;
    }
    navigate('/projects');
  };

  const getTotalRevenue = () => {
    const total = projects?.reduce((sum, p) => sum + parseFloat(p?.budgetTotal || 0), 0);
    if (total >= 1000000) return `$${(total / 1000000)?.toFixed(1)}M`;
    if (total >= 1000) return `$${(total / 1000)?.toFixed(0)}K`;
    return `$${total?.toFixed(0)}`;
  };

  const getNewLeadsThisWeek = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo?.setDate(oneWeekAgo?.getDate() - 7);
    return customers?.filter(c => {
      const created = c?.createdAt ? new Date(c.createdAt) : null;
      return created && created >= oneWeekAgo;
    })?.length || 0;
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'create': return 'Plus';
      case 'update': return 'Edit';
      case 'delete': return 'Trash2';
      case 'login': return 'LogIn';
      case 'logout': return 'LogOut';
      case 'invite': return 'UserPlus';
      case 'role_change': return 'Shield';
      default: return 'Activity';
    }
  };

  const getActivityColor = (action) => {
    switch (action) {
      case 'create': return 'text-success bg-success/10';
      case 'update': return 'text-accent bg-accent/10';
      case 'delete': return 'text-error bg-error/10';
      case 'login': case 'logout': return 'text-primary bg-primary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatActivityTime = (createdAt) => {
    if (!createdAt) return '';
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatActivityLabel = (activity) => {
    const user = activity?.user_profiles?.full_name || 'Someone';
    const action = activity?.action || 'updated';
    const resource = activity?.resource_type || 'record';
    return `${user} ${action}d a ${resource}`;
  };

  const metrics = [
    {
      title: 'Active Projects',
      value: loading ? '...' : projects?.length?.toString(),
      subtitle: `${projects?.filter(p => p?.phase === 'construction' || p?.phase === 'framing' || p?.phase === 'foundation')?.length || 0} in build phase`,
      icon: 'Briefcase',
      trend: 'up',
      trendValue: `+${projects?.filter(p => {
        const created = p?.createdAt ? new Date(p.createdAt) : null;
        const oneMonthAgo = new Date(); oneMonthAgo?.setMonth(oneMonthAgo?.getMonth() - 1);
        return created && created >= oneMonthAgo;
      })?.length || 0}`,
      variant: 'primary'
    },
    {
      title: 'Customers',
      value: loading ? '...' : customers?.length?.toString(),
      subtitle: `${getNewLeadsThisWeek()} new this week`,
      icon: 'Users',
      trend: 'up',
      trendValue: `+${getNewLeadsThisWeek()}`,
      variant: 'accent'
    },
    {
      title: 'Permit Deadlines',
      value: loading ? '...' : upcomingDeadlines?.filter(d => d?.type === 'permit')?.length?.toString(),
      subtitle: 'Next 30 days',
      icon: 'FileCheck',
      trend: upcomingDeadlines?.filter(d => d?.urgency === 'critical')?.length > 0 ? 'down' : 'up',
      trendValue: `${upcomingDeadlines?.filter(d => d?.urgency === 'critical' || d?.urgency === 'high')?.length} urgent`,
      variant: 'warning'
    },
    {
      title: 'Pipeline Value',
      value: loading ? '...' : getTotalRevenue(),
      subtitle: 'Total project budgets',
      icon: 'DollarSign',
      trend: 'up',
      trendValue: '+12%',
      variant: 'success'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Dashboard — ADU Project Overview"
        description="View your ADU construction pipeline at a glance. Track active projects, upcoming permit deadlines, revenue metrics, and team activity in your ADU Flow dashboard."
        canonical="/dashboard"
        keywords="ADU dashboard, construction management, project tracking, permit deadlines"
        noIndex={true}
      />
      <Header />
      <main className="main-content">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-2">
                Dashboard
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Welcome back! Here&apos;s your ADU construction overview
              </p>
            </div>
            {canCreate && (
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleNewProject}
                className="w-full sm:w-auto min-h-[44px]">
                New Project
              </Button>
            )}
          </div>

          {showPermDenied && (
            <PermissionDenied message="You have viewer access only. Contact an admin to create projects." />
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {loading ? (
              <SkeletonCard variant="metrics" count={4} />
            ) : (
              metrics?.map((metric, index) =>
                <MetricsCard key={index} {...metric} />
              )
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Active Projects Section */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground">
                  Active Projects
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => navigate('/projects')}
                  className="min-h-[44px]">
                  View All
                </Button>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 gap-4 sm:gap-5">
                  <SkeletonCard variant="project" count={3} />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:gap-5">
                  {projects?.slice(0, 6)?.map((project) =>
                    <ProjectCard key={project?.id} project={project} />
                  )}
                  {projects?.length === 0 && (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                      <EmptyState
                        variant="dashboard-projects"
                        canCreate={canCreate}
                        onAction={handleNewProject}
                        actionLabel="New Project"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Revenue Chart */}
              <RevenueChart data={revenueData} loading={loading} />
            </div>

            {/* Sidebar Section */}
            <div className="space-y-5 sm:space-y-6">
              {/* Upcoming Deadlines */}
              <div className="bg-card border border-border rounded-xl p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Clock" size={20} />
                    Upcoming Deadlines
                  </h3>
                  <span className="text-xs sm:text-sm text-error font-medium">
                    {upcomingDeadlines?.filter((d) => d?.urgency === 'critical' || d?.urgency === 'high')?.length} urgent
                  </span>
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3]?.map((i) => (
                      <div key={i} className="p-4 bg-muted rounded-lg animate-pulse">
                        <div className="h-4 bg-card rounded w-3/4 mb-2" />
                        <div className="h-3 bg-card rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {upcomingDeadlines?.map((deadline) =>
                      <DeadlineItem key={deadline?.id} deadline={deadline} />
                    )}
                    {upcomingDeadlines?.length === 0 && (
                      <EmptyState 
                        variant="dashboard-deadlines"
                        actionLabel=""
                        onAction={() => {}}
                        canCreate={false}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Recent Customers */}
              <div className="bg-card border border-border rounded-xl p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Users" size={20} />
                    Recent Customers
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    onClick={() => navigate('/customers')}
                    className="min-h-[44px]"
                  />
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3]?.map((i) => (
                      <div key={i} className="p-3 bg-muted rounded-lg animate-pulse">
                        <div className="h-4 bg-card rounded w-3/4 mb-2" />
                        <div className="h-3 bg-card rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {customers?.slice(0, 5)?.map((customer) => (
                      <div
                        key={customer?.id}
                        className="flex items-start gap-3 p-3 bg-background border border-border rounded-lg smooth-transition hover:shadow-md hover:border-primary/30 cursor-pointer"
                        onClick={() => navigate('/customers')}
                      >
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {customer?.avatar ? (
                            <img src={customer?.avatar} alt={customer?.name} className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <Icon name="User" size={16} className="text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground line-clamp-1">{customer?.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                              <Icon name="Home" size={10} />
                              {customer?.aduType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'N/A'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {customer?.createdAt ? new Date(customer.createdAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{customer?.email}</p>
                        </div>
                        <Icon name="ChevronRight" size={14} className="text-muted-foreground flex-shrink-0 mt-1" />
                      </div>
                    ))}
                    {customers?.length === 0 && (
                      <EmptyState
                        variant="dashboard-customers"
                        actionLabel="View Customers"
                        onAction={() => navigate('/customers')}
                        canCreate={true}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div className="bg-card border border-border rounded-xl p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Activity" size={20} />
                    Recent Activity
                  </h3>
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3]?.map((i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-muted animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="h-3.5 bg-muted animate-pulse rounded w-3/4" />
                          <div className="h-3 bg-muted animate-pulse rounded w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity?.map((activity) => (
                      <div key={activity?.id} className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity?.action)}`}>
                          <Icon name={getActivityIcon(activity?.action)} size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground line-clamp-2">{formatActivityLabel(activity)}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{formatActivityTime(activity?.created_at)}</p>
                        </div>
                      </div>
                    ))}
                    {recentActivity?.length === 0 && (
                      <EmptyState 
                        variant="dashboard-activity"
                        actionLabel=""
                        onAction={() => {}}
                        canCreate={false}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <QuickAddMenu />
    </div>
  );
};

export default Dashboard;